import { useState } from "react";
import { useNavigate } from "react-router";
import { AdminLayout } from "../../../components/admin/AdminLayout";
import { authedFetch } from "../../../lib/api-client";
import { useT } from "../../../lib/use-t";
import { SCHRIJVEN_TASK_TYPES, SCHRIJVEN_TASK_LABELS } from "@a2/core";
import type { SchrijvenTaskType } from "@a2/core";

export function AdminSchrijvenVariantNewPage() {
  const t = useT();
  const navigate = useNavigate();
  const [taskType, setTaskType] = useState<SchrijvenTaskType>("email");
  const [scenario, setScenario] = useState("");
  const [bulletsText, setBulletsText] = useState("");
  const [recipient, setRecipient] = useState("");
  const [greeting, setGreeting] = useState("");
  const [closing, setClosing] = useState("");
  const [status, setStatus] = useState<"curated" | "ai_pending" | "live" | "retired">("curated");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const bullets = bulletsText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!scenario.trim() || bullets.length === 0) {
      setError(t("admin.schrijven.validationRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await authedFetch("/admin/schrijven/variants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taskType, scenario: scenario.trim(), bullets,
          recipient: recipient.trim() || null,
          greeting: greeting.trim() || null,
          closing: closing.trim() || null,
          status,
        }),
      });
      const body = await res.json();
      if (!body.ok) { setError(body.error?.message ?? "Fout"); return; }
      await navigate("/admin/schrijven/variants");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Fout");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
            {t("admin.schrijven.variantNewEyebrow")}
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-[var(--ink)] mt-1">
            {t("admin.schrijven.variantNewTitle")}
          </h1>
        </div>

        {error ? <p role="alert" className="text-sm text-[var(--fail)]">{error}</p> : null}

        <div className="space-y-4">
          <label className="block space-y-1">
            <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">{t("admin.schrijven.fieldType")}</span>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value as SchrijvenTaskType)}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
            >
              {SCHRIJVEN_TASK_TYPES.map((taskType) => (
                <option key={taskType} value={taskType}>{SCHRIJVEN_TASK_LABELS[taskType]}</option>
              ))}
            </select>
          </label>

          <label className="block space-y-1">
            <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">{t("admin.schrijven.fieldScenario")}</span>
            <textarea
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--ink)] resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
            />
          </label>

          <label className="block space-y-1">
            <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
              {t("admin.schrijven.fieldBullets")}
            </span>
            <textarea
              value={bulletsText}
              onChange={(e) => setBulletsText(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--ink)] resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
              placeholder={t("admin.schrijven.fieldBulletsPlaceholder")}
            />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block space-y-1">
              <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">{t("admin.schrijven.fieldRecipient")}</span>
              <input
                type="text"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                placeholder={t("admin.schrijven.fieldRecipientPlaceholder")}
              />
            </label>

            <label className="block space-y-1">
              <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">{t("admin.schrijven.fieldStatus")}</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
                className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
              >
                <option value="curated">{t("admin.schrijven.statusCurated")}</option>
                <option value="ai_pending">{t("admin.schrijven.statusAiPending")}</option>
                <option value="live">{t("admin.schrijven.statusLive")}</option>
                <option value="retired">{t("admin.schrijven.statusRetired")}</option>
              </select>
            </label>
          </div>

          <label className="block space-y-1">
            <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">{t("admin.schrijven.fieldGreeting")}</span>
            <input
              type="text"
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
              placeholder={t("admin.schrijven.fieldGreetingPlaceholder")}
            />
          </label>

          <label className="block space-y-1">
            <span className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">{t("admin.schrijven.fieldClosing")}</span>
            <input
              type="text"
              value={closing}
              onChange={(e) => setClosing(e.target.value)}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--panel)] px-3 py-2 text-sm text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
              placeholder={t("admin.schrijven.fieldClosingPlaceholder")}
            />
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate("/admin/schrijven/variants")}
            className="inline-flex h-10 items-center rounded-[var(--radius)] border border-[var(--border)] bg-transparent px-5 text-sm text-[var(--ink-muted)] hover:bg-[var(--panel-soft)] transition-colors"
          >
            {t("admin.schrijven.cancelButton")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex h-10 items-center rounded-[var(--radius)] bg-[var(--accent)] px-5 text-sm font-medium text-white hover:bg-[var(--accent-hover)] disabled:opacity-50 transition-colors"
          >
            {saving ? t("admin.schrijven.savingButton") : t("admin.schrijven.saveButton")}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
